import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  Signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs';
import { AppRoutes, DrillsRoutes, NAVIGATION_TOKEN, NavigationProvider, TranslationService } from '@core';
import {
  SzAppShellComponent,
  SzButtonComponent,
  SzCtaBarComponent,
  SzIconComponent,
  SzPageTitleComponent,
  SzSkeletonComponent,
  SzSuccessOverlayService,
  SzTopBarComponent
} from '@ui';
import { DrillDetailQueryHandler, DrillDetailQueryResult, UpdateDrillCommandHandler } from '../../application/handlers';
import { DrillDetailViewModel } from '../../application/models';
import { DrillFormComponent } from '../drill-form/drill-form.component';
import { DrillFormValue } from '../drill-form/drill-form-value';

@Component({
  selector: 'lib-drill-edit',
  templateUrl: './drill-edit.component.html',
  styleUrls: ['./drill-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    SzAppShellComponent,
    SzTopBarComponent,
    SzPageTitleComponent,
    SzCtaBarComponent,
    SzButtonComponent,
    SzIconComponent,
    SzSkeletonComponent,
    DrillFormComponent
  ],
  providers: [DrillDetailQueryHandler, UpdateDrillCommandHandler]
})
export class DrillEditComponent implements OnInit {
  private readonly form: Signal<DrillFormComponent | undefined> = viewChild(DrillFormComponent);

  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly navigation: NavigationProvider = inject(NAVIGATION_TOKEN);
  private readonly detailHandler: DrillDetailQueryHandler = inject(DrillDetailQueryHandler);
  private readonly updateHandler: UpdateDrillCommandHandler = inject(UpdateDrillCommandHandler);
  private readonly translation: TranslationService = inject(TranslationService);
  private readonly successOverlay: SzSuccessOverlayService = inject(SzSuccessOverlayService);

  readonly drill: WritableSignal<DrillDetailViewModel | null> = signal<DrillDetailViewModel | null>(null);
  readonly canSubmit: Signal<boolean> = computed((): boolean => this.form()?.canSubmit() ?? false);

  private drillId: string = '';

  ngOnInit(): void {
    this.drillId = this.route.snapshot.paramMap.get('id') ?? '';
    this.detailHandler
      .query(this.drillId)
      .pipe(take(1))
      .subscribe((r: DrillDetailQueryResult) => this.drill.set(r.drill));
  }

  onSubmit(value: DrillFormValue): void {
    this.successOverlay
      .showDuring$(this.updateHandler.execute({ drillId: this.drillId, changes: value }), {
        loadingTitle: this.translation.translate('drills.edit.saving'),
        successTitle: this.translation.translate('drills.edit.success')
      })
      .pipe(take(1))
      .subscribe(() => this.navigation.replace(['/', AppRoutes.DRILLS, DrillsRoutes.DETAIL, this.drillId]));
  }

  triggerSubmit(): void {
    this.form()?.submit();
  }
}
